import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';
import { LogOut, Moon, Sun, Layout, BookOpen, Users, HeartHandshake, Mic, IceCream, Languages } from 'lucide-react';
import { getPaneGroups } from './availablePanes';

/**
 * Mobile Pane Selector Component
 * Slide-out menu for selecting study tools on mobile
 */
const PaneSelector = ({ 
  isOpen, 
  onClose, 
  availablePanes, 
  currentLayout, 
  onPaneSelect, 
  onLogout,
  onOpenLayoutCustomizer
}) => {
  const navigate = useNavigate();
  const { language, toggleLanguage } = useLanguage();
  const es = language === 'es';
  const navLinks = [
    { icon: BookOpen, label: es ? 'Biblia' : 'Bible', to: '/bible' },
    { icon: Users, label: es ? 'Comunidad' : 'Community', to: '/community' },
    { icon: HeartHandshake, label: es ? 'Oración' : 'Prayer', to: '/prayer' },
    { icon: Mic, label: es ? 'Preparar Sermón' : 'Sermon Prep', to: '/sermon-prep' },
    { icon: IceCream, label: es ? 'Sundae diario' : 'Daily Sundae', to: '/community?tab=games&sundae=start' },
  ];
  const [isDark, setIsDark] = React.useState(() => {
    return document.documentElement.classList.contains('dark');
  });

  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    if (newTheme) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  // Map pane IDs to routes
  const getRouteForPane = (paneId) => {
    const routeMap = {
      profile: '/profile',
      maps: '/maps',
      wordstudy: '/word-study',
      concordance: '/concordance',
      commentary: '/commentary',
      notes: '/notes',
      dictionary: '/dictionary',
      characters: '/characters',
      devotional: '/devotional',
      bookmarks: '/bookmarks',
      crossrefs: '/cross-references',
      'reading-plans': '/reading-plans',
      highlights: '/highlights',
      'verse-image': '/verse-image',
      parallel: '/parallel',
      comparison: '/comparison',
      interlinear: '/interlinear',
      votd: '/verse-of-the-day',
      companion: '/ai-companion',
      videos: '/videos',
      notifications: '/notifications',
      chronology: '/chronology',
      atlas: '/atlas',
      harmony: '/gospel-harmony',
      topical: '/topical',
      'historic-plans': '/historic-plans',
      apocrypha: '/apocrypha',
      'original-languages': '/original-languages',
      isbe: '/isbe',
      'study-paths': '/study-paths',
      bible: '/bible',
      ai: '/ai-insights',
    };
    return routeMap[paneId] || null;
  };

  const handlePaneClick = (paneId) => {
    const route = getRouteForPane(paneId);
    if (route) {
      // Navigate to dedicated route
      navigate(route);
      onClose();
    } else {
      // Fallback: some tools might not have routes yet (rare)
      onPaneSelect(paneId);
    }
  };

  const groups = getPaneGroups(language);

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent 
        side="left" 
        className="w-[280px] sm:w-[350px] bg-background text-foreground border-r border-border"
      >
        <SheetHeader>
          <SheetTitle className="text-foreground text-2xl font-bold">
            {language === 'es' ? 'Herramientas de Estudio' : 'Study Tools'}
          </SheetTitle>
          <SheetDescription className="text-muted-foreground">
            {language === 'es' 
              ? 'Selecciona cualquier herramienta para abrir'
              : 'Select any tool to open'}
          </SheetDescription>
        </SheetHeader>
        
        <div className="mt-6 space-y-4 max-h-[72vh] overflow-y-auto pr-1">
          {/* Quick navigation (mirrors the header buttons hidden on mobile) */}
          <div data-testid="mobile-nav-links">
            <p className="px-2 mb-1 text-[11px] font-semibold uppercase tracking-widest text-primary">
              {es ? 'Navegar' : 'Navigate'}
            </p>
            <div className="space-y-0.5">
              {navLinks.map(({ icon: Icon, label, to }) => (
                <Button
                  key={to}
                  variant="ghost"
                  className="w-full justify-start text-left text-foreground hover:bg-primary/10"
                  onClick={() => { navigate(to); onClose(); }}
                  data-testid={`mobile-nav-${to.replace(/[^a-z]/gi, '')}`}
                >
                  <Icon className="mr-2 h-4 w-4" /> {label}
                </Button>
              ))}
            </div>
          </div>

          {groups.map((group) => (
            <div key={group.key} data-testid={`pane-group-${group.key}`}>
              <p className="px-2 mb-1 text-[11px] font-semibold uppercase tracking-widest text-primary">
                {group.label}
              </p>
              <div className="space-y-0.5">
                {group.panes.map((pane) => (
                  <Button
                    key={pane.id}
                    variant="ghost"
                    className="w-full justify-start text-left text-foreground hover:bg-primary/10"
                    onClick={() => handlePaneClick(pane.id)}
                    data-testid={`pane-menu-${pane.id}`}
                  >
                    {pane.label}
                  </Button>
                ))}
              </div>
            </div>
          ))}
          
          <div className="pt-4 border-t border-border space-y-1">
            <Button
              variant="ghost"
              className="w-full justify-start text-foreground hover:bg-primary/10 font-medium text-base"
              onClick={() => {
                onClose();
                onOpenLayoutCustomizer?.();
              }}
            >
              <Layout className="mr-2 h-5 w-5" />
              {language === 'es' ? 'Personalizar Diseño' : 'Customize Layout'}
            </Button>
            
            <Button
              variant="ghost"
              className="w-full justify-start text-foreground hover:bg-primary/10 font-medium text-base"
              onClick={toggleTheme}
            >
              {isDark ? <Sun className="mr-2 h-5 w-5" /> : <Moon className="mr-2 h-5 w-5" />}
              {language === 'es' ? (isDark ? 'Modo Claro' : 'Modo Oscuro') : (isDark ? 'Light Mode' : 'Dark Mode')}
            </Button>
            
            <Button
              variant="ghost"
              className="w-full justify-start text-foreground hover:bg-primary/10 font-medium text-base"
              onClick={toggleLanguage}
              data-testid="mobile-language-toggle"
            >
              <Languages className="mr-2 h-5 w-5" />
              {es ? 'English' : 'Español'}
            </Button>

            <Button
              variant="ghost"
              className="w-full justify-start text-destructive hover:bg-destructive/10 font-medium text-base"
              onClick={onLogout}
            >
              <LogOut className="mr-2 h-5 w-5" />
              {language === 'es' ? 'Cerrar Sesión' : 'Logout'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default PaneSelector;
